/**
 * One reducer for the whole table.
 *
 * v1 spread this across five hooks that each returned a positional array —
 * `useEvents` returned eleven elements — so adding an operation meant editing
 * every consumer. Every edit is now an action, which also gives sync something
 * precise to send: each action marks the slices it touched in `outbox`, and the
 * sync layer drains it. That is what makes concurrent editing safe, because we
 * push the cells that changed rather than overwriting the whole document.
 */

import {
  cellKey,
  clampWeight,
  defaultSettings,
  newId,
  normalizeSettings,
  normalizeTable,
} from '../data/model';
import { occurrences } from '../data/recurrence';
import { buildApplicability } from '../data/selectors';

export function initialState(table) {
  return { table, outbox: emptyOutbox() };
}

/**
 * What still needs sending. The structural slices are booleans — they are sent
 * whole — while `attendance` is the set of individual cells that changed, so
 * two people marking different rows never overwrite each other.
 * `attendanceReplace` is the exception: the whole column set is being replaced,
 * so the document is overwritten rather than merged.
 */
function emptyOutbox() {
  return { roster: false, schedule: false, settings: false, attendance: {}, attendanceReplace: false };
}

const mark = (outbox, ...slices) => {
  const next = { ...outbox };
  for (const slice of slices) next[slice] = true;
  return next;
};

const markCells = (outbox, cells) => ({
  ...outbox,
  attendance: { ...outbox.attendance, ...cells },
});

export function tableReducer(state, action) {
  const { table, outbox } = state;

  switch (action.type) {
    /* ---------------------------------------------------------------- people */

    case 'people/add': {
      const people = action.names.map((name) => ({ id: newId('p'), name, aliases: [] }));
      if (people.length === 0) return state;
      return {
        table: { ...table, people: [...table.people, ...people] },
        outbox: mark(outbox, 'roster'),
      };
    }

    /** Records another spelling of someone's name, for matching pasted lists. */
    case 'people/setAliases': {
      return {
        table: {
          ...table,
          people: table.people.map((person) =>
            person.id === action.id ? { ...person, aliases: action.aliases } : person
          ),
        },
        outbox: mark(outbox, 'roster'),
      };
    }

    /**
     * Folds one person into another: alias lists, group memberships, and any
     * marks the loser has that the winner does not. The old rosters are full of
     * the same student under two spellings, so this is the cleanup after an
     * import.
     */
    case 'people/merge': {
      const winner = table.people.find((p) => p.id === action.keepId);
      const loser = table.people.find((p) => p.id === action.mergeId);
      if (!winner || !loser || winner.id === loser.id) return state;

      const aliases = Array.from(
        new Set([...(winner.aliases || []), ...(loser.aliases || []), loser.name])
      );
      const attendance = { ...table.attendance };
      const cells = {};

      for (const event of table.events) {
        const from = cellKey(loser.id, event.id);
        const to = cellKey(winner.id, event.id);
        if (!(from in attendance)) continue;
        // The kept person's own marks win; only their gaps are filled.
        if (!(to in attendance)) {
          attendance[to] = attendance[from];
          cells[to] = attendance[from];
        }
        delete attendance[from];
        cells[from] = null;
      }

      return {
        table: {
          ...table,
          people: table.people
            .filter((p) => p.id !== loser.id)
            .map((p) => (p.id === winner.id ? { ...p, aliases } : p)),
          groups: table.groups.map((group) => ({
            ...group,
            memberIds: group.memberIds.includes(loser.id)
              ? Array.from(new Set(group.memberIds.map((id) => (id === loser.id ? winner.id : id))))
              : group.memberIds,
          })),
          attendance,
        },
        outbox: markCells(mark(outbox, 'roster'), cells),
      };
    }

    case 'people/rename': {
      return {
        table: {
          ...table,
          people: table.people.map((p) => (p.id === action.id ? { ...p, name: action.name } : p)),
        },
        outbox: mark(outbox, 'roster'),
      };
    }

    case 'people/remove': {
      // Drop their attendance too. v1 left these behind forever and shipped the
      // orphans to the cloud on every write.
      const cells = {};
      for (const event of table.events) cells[cellKey(action.id, event.id)] = null;
      return {
        table: {
          ...table,
          people: table.people.filter((p) => p.id !== action.id),
          groups: table.groups.map((g) => ({
            ...g,
            memberIds: g.memberIds.filter((id) => id !== action.id),
          })),
          attendance: without(table.attendance, Object.keys(cells)),
        },
        outbox: markCells(mark(outbox, 'roster'), cells),
      };
    }

    /* ---------------------------------------------------------------- groups */

    case 'groups/replace': {
      const known = new Set(table.people.map((p) => p.id));
      const groups = action.groups.map((group) => ({
        ...group,
        memberIds: group.memberIds.filter((id) => known.has(id)),
      }));

      // A weekly folder and the group that attends it are named alike, and
      // renaming one has to rename the other. CIR renamed an empty "Friday 2pm"
      // group to "Friday 11am" and filled it; its folder went on saying
      // "Friday 2pm" over the 11am students, and the real 2pm students were
      // never given a folder at all. A folder someone has named differently
      // from its group is left alone.
      const renamed = new Map();
      for (const group of groups) {
        const before = table.groups.find((entry) => entry.id === group.id);
        if (before && group.name.trim() && before.name.trim() !== group.name.trim()) {
          renamed.set(group.id, { from: before.name.trim(), to: group.name.trim() });
        }
      }
      let folders = table.folders;
      if (renamed.size > 0) {
        folders = table.folders.map((folder) => {
          const change = folder.groupId && renamed.get(folder.groupId);
          return change && folder.name.trim() === change.from ? { ...folder, name: change.to } : folder;
        });
      }
      const foldersChanged = folders.some((folder, index) => folder !== table.folders[index]);

      return {
        table: { ...table, groups, folders },
        outbox: foldersChanged ? mark(outbox, 'roster', 'schedule') : mark(outbox, 'roster'),
      };
    }

    /* ----------------------------------------------------------------- terms */

    case 'terms/add': {
      const term = {
        id: newId('t'),
        name: action.name,
        startDate: action.startDate || null,
        endDate: action.endDate || null,
      };
      return {
        table: { ...table, terms: sortTerms([...table.terms, term]) },
        outbox: mark(outbox, 'schedule'),
      };
    }

    case 'terms/update': {
      return {
        table: {
          ...table,
          terms: sortTerms(
            table.terms.map((term) => (term.id === action.id ? { ...term, ...action.changes } : term))
          ),
        },
        outbox: mark(outbox, 'schedule'),
      };
    }

    case 'terms/remove': {
      // The events survive and simply stop belonging to a term, so removing one
      // never destroys a semester's attendance.
      return {
        table: {
          ...table,
          terms: table.terms.filter((term) => term.id !== action.id),
          events: table.events.map((e) => (e.termId === action.id ? { ...e, termId: null } : e)),
        },
        outbox: mark(outbox, 'schedule'),
      };
    }

    /* --------------------------------------------------------------- folders */

    case 'folders/add': {
      const folder = {
        id: newId('f'),
        name: action.name,
        isOpen: true,
        parentId: action.parentId || null,
        groupId: action.groupId || null,
      };
      return {
        table: { ...table, folders: [...table.folders, folder] },
        outbox: mark(outbox, 'schedule'),
      };
    }

    /** Moves a folder into a section, or back out to the top level. */
    case 'folders/setParent': {
      // A folder with children cannot itself be filed inside one; two levels is
      // what the header can draw.
      const hasChildren = table.folders.some((f) => f.parentId === action.id);
      const target = table.folders.find((f) => f.id === action.parentId);
      if (action.parentId && (hasChildren || !target || target.parentId || target.id === action.id)) {
        return state;
      }
      return {
        table: {
          ...table,
          folders: table.folders.map((f) =>
            f.id === action.id ? { ...f, parentId: action.parentId || null } : f
          ),
        },
        outbox: mark(outbox, 'schedule'),
      };
    }

    /** Points a series of sessions at the cohort that attends it, or at nobody. */
    case 'folders/setGroup': {
      return {
        table: {
          ...table,
          folders: table.folders.map((f) =>
            f.id === action.id ? { ...f, groupId: action.groupId || null } : f
          ),
        },
        outbox: mark(outbox, 'schedule'),
      };
    }

    case 'folders/rename': {
      // The other half of keeping a weekly folder and its group named alike —
      // see groups/replace. The group follows only if it carried the folder's
      // old name, and so does any other folder it attends under that name.
      const renamedFolder = table.folders.find((f) => f.id === action.id);
      const from = renamedFolder?.name.trim();
      const to = (action.name || '').trim();
      const group = renamedFolder?.groupId && table.groups.find((g) => g.id === renamedFolder.groupId);
      const follows = Boolean(group && to && from !== to && group.name.trim() === from);

      return {
        table: {
          ...table,
          folders: table.folders.map((f) =>
            f.id === action.id || (follows && f.groupId === group.id && f.name.trim() === from)
              ? { ...f, name: action.name }
              : f
          ),
          groups: follows
            ? table.groups.map((g) => (g.id === group.id ? { ...g, name: to } : g))
            : table.groups,
        },
        outbox: follows ? mark(outbox, 'schedule', 'roster') : mark(outbox, 'schedule'),
      };
    }

    case 'folders/toggle': {
      // Deliberately does not mark the outbox: whether a folder is collapsed is
      // a view preference like the filters and the sort, and pushing it would
      // fold the folder shut under everyone else mid-meeting.
      return {
        table: {
          ...table,
          folders: table.folders.map((f) => (f.id === action.id ? { ...f, isOpen: !f.isOpen } : f)),
        },
        outbox,
      };
    }

    case 'folders/remove': {
      // Deleting a folder releases its events, and lifts any folders inside it
      // to the top rather than taking them down with it. v1 did the reverse —
      // deleting the last event in a folder silently deleted the folder too.
      return {
        table: {
          ...table,
          folders: table.folders
            .filter((f) => f.id !== action.id)
            .map((f) => (f.parentId === action.id ? { ...f, parentId: null } : f)),
          events: table.events.map((e) => (e.folderId === action.id ? { ...e, folderId: null } : e)),
        },
        outbox: mark(outbox, 'schedule'),
      };
    }

    /* ---------------------------------------------------------------- events */

    case 'events/add': {
      // The dialog names the event up front so the grid can scroll to it. That
      // also makes a double-click on Add one event rather than two.
      if (action.id && table.events.some((event) => event.id === action.id)) return state;

      let folders = table.folders;
      let folderId = action.folderId || null;

      if (action.newFolderName) {
        const folder = {
          id: action.newFolderId || newId('f'),
          name: action.newFolderName,
          isOpen: true,
          parentId: action.folderParentId || null,
          groupId: action.folderGroupId || null,
        };
        folders = [...folders, folder];
        folderId = folder.id;
      }

      const event = {
        id: action.id || newId('e'),
        name: action.name,
        weight: clampWeight(action.weight),
        folderId,
        termId: action.termId || null,
        startDate: action.startDate || null,
        endDate: action.endDate || null,
      };
      return {
        table: { ...table, folders, events: [...table.events, event] },
        outbox: mark(outbox, 'schedule'),
      };
    }

    /**
     * Builds a whole semester of a weekly meeting at once.
     *
     * Every check-in block in the old spreadsheets is exactly this shape — one
     * weekday, fifteen weeks — and every one was typed out by hand. One of them
     * has a date from the wrong semester in the middle of it.
     */
    case 'events/addRecurring': {
      const known = new Set(table.events.map((event) => event.id));
      if ((action.ids || []).some((id) => known.has(id))) return state;

      let folders = table.folders;
      let folderId = action.folderId || null;

      if (action.newFolderName) {
        const folder = {
          id: action.newFolderId || newId('f'),
          name: action.newFolderName,
          isOpen: true,
          parentId: action.folderParentId || null,
          groupId: action.folderGroupId || null,
        };
        folders = [...folders, folder];
        folderId = folder.id;
      }

      const dates = occurrences({
        repeats: action.repeats || 'weekly',
        startDate: action.startDate,
        endDate: action.endDate,
        weekday: action.weekday,
        skip: action.skipDates,
      });
      if (dates.length === 0) return state;

      const events = dates.map((date, index) => ({
        id: action.ids?.[index] || newId('e'),
        name: action.name,
        weight: clampWeight(action.weight),
        folderId,
        termId: action.termId || null,
        startDate: date,
        endDate: null,
      }));

      return {
        table: { ...table, folders, events: [...table.events, ...events] },
        outbox: mark(outbox, 'schedule'),
      };
    }

    /**
     * Applies a whole imported grid at once — new people, groups, folders,
     * terms, events and every mark — as a single action, so it is one step and
     * one sync write rather than hundreds.
     */
    case 'table/import': {
      const payload = action.payload || {};
      const attendance = payload.attendance || {};

      // Sessions that already existed come back as updates rather than
      // additions, so the term chosen in the dialog applies to every column
      // being imported and not only to the ones it had to create.
      const updates = new Map((payload.updatedEvents || []).map((event) => [event.id, event]));
      const events = table.events.map((event) => updates.get(event.id) || event);

      return {
        table: {
          ...table,
          people: [...table.people, ...(payload.people || [])],
          groups: mergeGroups(table.groups, payload.groups || []),
          folders: [...table.folders, ...(payload.folders || [])],
          terms: sortTerms([...table.terms, ...(payload.terms || [])]),
          events: [...events, ...(payload.events || [])],
          attendance: { ...table.attendance, ...attendance },
        },
        outbox: markCells(mark(outbox, 'roster', 'schedule'), attendance),
      };
    }

    case 'events/update': {
      const changes = { ...action.changes };
      if ('weight' in changes) changes.weight = clampWeight(changes.weight);
      return {
        table: {
          ...table,
          events: table.events.map((e) => (e.id === action.id ? { ...e, ...changes } : e)),
        },
        outbox: mark(outbox, 'schedule'),
      };
    }

    case 'events/remove': {
      const cells = {};
      for (const person of table.people) cells[cellKey(person.id, action.id)] = null;
      return {
        table: {
          ...table,
          events: table.events.filter((e) => e.id !== action.id),
          attendance: without(table.attendance, Object.keys(cells)),
        },
        outbox: markCells(mark(outbox, 'schedule'), cells),
      };
    }

    /* ------------------------------------------------------------ attendance */

    case 'attendance/set':
      return applyCells(state, [action]);

    case 'attendance/setMany':
      return applyCells(state, action.entries);

    case 'attendance/fillColumn': {
      // Only fills blanks, so it never overwrites a mark somebody made by hand.
      // A cell holding a status that has since been deleted counts as blank: it
      // shows as one, and counts as nothing.
      const known = new Set(table.settings.statuses.map((status) => status.id));
      const entries = action.personIds
        .filter((personId) => !known.has(table.attendance[cellKey(personId, action.eventId)]))
        .map((personId) => ({ personId, eventId: action.eventId, statusId: action.statusId }));
      return applyCells(state, entries);
    }

    case 'attendance/clearColumn': {
      const entries = action.personIds.map((personId) => ({
        personId,
        eventId: action.eventId,
        statusId: null,
      }));
      return applyCells(state, entries);
    }

    /* -------------------------------------------------------------- settings */

    case 'settings/update': {
      return {
        table: { ...table, settings: { ...table.settings, ...action.changes } },
        outbox: mark(outbox, 'settings'),
      };
    }

    case 'settings/setStatuses': {
      // Cells holding a status that no longer exists become unmarked, so scores
      // never silently reference a status nobody can see.
      const kept = new Set(action.statuses.map((s) => s.id));
      const cells = {};
      for (const [key, statusId] of Object.entries(table.attendance)) {
        if (!kept.has(statusId)) cells[key] = null;
      }
      return {
        table: {
          ...table,
          settings: { ...table.settings, statuses: action.statuses },
          attendance: without(table.attendance, Object.keys(cells)),
        },
        outbox: markCells(mark(outbox, 'settings'), cells),
      };
    }

    /* ----------------------------------------------------------------- table */

    /** Loading a table: from the cloud, from storage, or switching between them. */
    case 'table/replace':
      return { table: normalizeTable(action.table), outbox: emptyOutbox() };

    /**
     * Opening a shared table. `upgrade` is set when the cloud copy still uses
     * the old single-document layout: the whole table is queued for writing so
     * the per-slice documents get laid down once, rather than appearing one at a
     * time as edits happen and leaving other clients reading a half-migrated
     * table.
     */
    case 'table/adopt':
      return {
        table: normalizeTable(action.table),
        outbox: action.upgrade
          ? { roster: true, schedule: true, settings: true, attendance: {}, attendanceReplace: true }
          : emptyOutbox(),
      };

    /**
     * Empties the table but keeps its statuses. Attendance is marked for
     * wholesale replacement rather than as a set of changed cells, so the
     * shared copy is emptied too instead of keeping every deleted mark.
     */
    case 'table/clear':
      return {
        table: { ...table, people: [], groups: [], folders: [], events: [], attendance: {} },
        outbox: { ...mark(outbox, 'roster', 'schedule'), attendance: {}, attendanceReplace: true },
      };

    /**
     * Housekeeping after a remote merge. Keeps the outbox — unlike
     * `table/replace`, this is not a new table, just the same one tidied, and
     * dropping the outbox here would discard local edits that have not been
     * sent yet.
     */
    case 'table/prune':
      return { ...state, table: action.table };

    /**
     * A slice arrived from another device. Remote wins for that slice only —
     * the rest of the local table is untouched, which is why two people editing
     * different columns no longer overwrite each other.
     */
    case 'remote/merge': {
      // A cell edited here and not yet sent keeps its local value: the queued
      // write is about to overwrite the server with it. The sync layer filters
      // these too, but it reads the outbox as of the last render; the reducer
      // sees it as of this action, which closes the gap after a failed write
      // hands its cells back.
      let data = action.data;
      if (action.slice === 'attendance' && data && typeof data === 'object') {
        data = Object.fromEntries(Object.entries(data).filter(([key]) => !(key in outbox.attendance)));
      }
      const merged = mergeSlice(table, action.slice, data);
      if (merged === table) return state;

      // Only what this snapshot actually removed is pruned. The four slices are
      // separate documents whose snapshots arrive in no particular order, and
      // `lastAttendance` in the sync layer has already accepted every mark it
      // has seen, so a mark dropped here is never delivered again. Pruning
      // everything the local schedule did not know yet meant a browser with an
      // older schedule lost the newest folder's marks from its screen for
      // good — blanks that "Fill blanks" would then have written over.
      const cleaned = action.slice === 'attendance' ? merged : withoutRemoved(table, merged);

      // A queued edit to a cell that has just been pruned would put it straight
      // back on the server, pointing at something that no longer exists — so it
      // is sent as a deletion instead.
      const cancelled = Object.keys(merged.attendance).filter(
        (key) => !(key in cleaned.attendance) && typeof outbox.attendance[key] === 'string'
      );
      if (cancelled.length === 0) return { table: cleaned, outbox };
      return {
        table: cleaned,
        outbox: markCells(outbox, Object.fromEntries(cancelled.map((key) => [key, null]))),
      };
    }

    /**
     * A write failed. Queue each cell it carried again, at the value the table
     * shows now — the drain is optimistic, so without this a rejected write
     * silently discards the edits it was carrying.
     */
    case 'sync/requeue': {
      // What is queued again is what the table shows now, not what the failed
      // write carried: a later edit of the same cell may itself have been sent
      // and failed, and putting the older value back would send that instead.
      const attendance = { ...outbox.attendance };
      for (const key of Object.keys(action.cells || {})) {
        if (!(key in attendance)) attendance[key] = table.attendance[key] ?? null;
      }
      const next = { ...outbox, attendance };
      for (const slice of action.slices || []) next[slice] = true;
      if (action.attendanceReplace) next.attendanceReplace = true;
      return { ...state, outbox: next };
    }

    case 'sync/drained': {
      const attendance = { ...outbox.attendance };
      for (const key of action.cells || []) delete attendance[key];
      const next = { ...outbox, attendance };
      for (const slice of action.slices || []) next[slice] = false;
      if (action.attendanceReplace) next.attendanceReplace = false;
      return { ...state, outbox: next };
    }

    default:
      return state;
  }
}

/* -------------------------------------------------------------------------- */
/* helpers                                                                     */
/* -------------------------------------------------------------------------- */

const sortTerms = (terms) =>
  terms.slice().sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));

/** An imported group folds into the existing one it matches, by id or by name. */
function mergeGroups(existing, incoming) {
  const merged = existing.map((group) => ({ ...group }));
  const byId = new Map(merged.map((group) => [group.id, group]));
  const byName = new Map(merged.map((group) => [group.name.toLowerCase(), group]));
  const added = [];

  for (const group of incoming) {
    const match = byId.get(group.id) || byName.get(group.name.toLowerCase());
    if (match) {
      match.memberIds = Array.from(new Set([...match.memberIds, ...group.memberIds]));
      continue;
    }
    // Register as we go, so two incoming groups sharing a name fold together
    // instead of both landing.
    const copy = { ...group };
    added.push(copy);
    byId.set(copy.id, copy);
    byName.set(copy.name.toLowerCase(), copy);
  }

  return [...merged, ...added];
}

function without(source, keys) {
  if (keys.length === 0) return source;
  const next = { ...source };
  for (const key of keys) delete next[key];
  return next;
}

/**
 * Writes a batch of cells. `statusId: null` clears the cell, and clearing sends
 * an explicit null so the other end deletes it rather than keeping a stale mark.
 *
 * A mark on a session that is not the person's is refused. "Fill blanks" works
 * on the rows in view, which in the default view is the whole programme, so
 * filling one Tuesday date used to write a mark for every student in every
 * other session too — invisible, until one of them was moved into Tuesday and
 * it surfaced as attendance they never had.
 */
function applyCells(state, entries) {
  if (!entries || entries.length === 0) return state;

  const attendance = { ...state.table.attendance };
  const cells = {};
  let changed = false;
  const applies = buildApplicability(state.table);
  const eventsById = new Map(state.table.events.map((event) => [event.id, event]));

  for (const { personId, eventId, statusId } of entries) {
    const key = cellKey(personId, eventId);
    const next = statusId || null;
    if ((attendance[key] || null) === next) continue;
    const event = eventsById.get(eventId);
    if (next && event && !applies(personId, event)) continue;
    changed = true;
    cells[key] = next;
    if (next) attendance[key] = next;
    else delete attendance[key];
  }

  if (!changed) return state;
  return {
    table: { ...state.table, attendance },
    outbox: markCells(state.outbox, cells),
  };
}

/**
 * Splices a slice that arrived over the wire into the table.
 *
 * Everything here is untrusted. Joining a table runs the payload through
 * `normalizeTable`, but live updates arrive as raw snapshot data — so a
 * half-written or future-schema document would otherwise reach the renderer
 * unchecked, and something as small as `settings.statuses` not being an array
 * throws during render and blanks the page for every connected client.
 */
function mergeSlice(table, slice, data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return table;

  switch (slice) {
    case 'roster':
      return {
        ...table,
        people: Array.isArray(data.people) ? data.people : table.people,
        groups: Array.isArray(data.groups) ? data.groups : table.groups,
      };
    case 'schedule':
      return {
        ...table,
        folders: Array.isArray(data.folders) ? withLocalCollapse(data.folders, table.folders) : table.folders,
        events: Array.isArray(data.events) ? data.events : table.events,
        terms: Array.isArray(data.terms) ? data.terms : table.terms,
      };
    case 'settings':
      return {
        ...table,
        settings: normalizeSettings({ ...table.settings, ...data }, defaultSettings()),
      };
    case 'attendance': {
      // Field-level: only the keys the sender actually touched.
      const attendance = { ...table.attendance };
      for (const [key, value] of Object.entries(data)) {
        if (value === null) delete attendance[key];
        else if (typeof value === 'string') attendance[key] = value;
      }
      return { ...table, attendance };
    }
    default:
      return table;
  }
}

/**
 * Keeps each folder collapsed or expanded the way this viewer left it. The
 * incoming folders carry no `isOpen` at all, and a folder nobody has seen
 * before opens.
 */
function withLocalCollapse(incoming, local) {
  const wasOpen = new Map(local.map((folder) => [folder.id, folder.isOpen]));
  return incoming.map((folder) => ({
    ...folder,
    isOpen: wasOpen.has(folder.id) ? wasOpen.get(folder.id) : folder.isOpen !== false,
  }));
}

/**
 * Drops the cells of whatever a remote merge removed — a person or an event
 * that was in `before` and is not in `after`, or, when the settings arrived, a
 * status they do not name — so no cell outlives what it points at.
 *
 * Nothing else is touched. A mark naming an event or person this browser has
 * not received yet is not an orphan; it is on the way. Keys are rebuilt with
 * `cellKey`, never parsed.
 */
export function withoutRemoved(before, after) {
  const peopleNow = new Set(after.people.map((person) => person.id));
  const eventsNow = new Set(after.events.map((event) => event.id));
  const statusesNow = new Set(after.settings.statuses.map((status) => status.id));

  // Statuses are different: they all arrive in the one settings document, so a
  // settings snapshot is the whole list, and any mark holding a status it does
  // not name is dead — including one that arrived after the status was deleted.
  // Pruning only what this merge removed left such a mark on every screen for
  // good, drawn as a blank that Fill blanks would not fill.
  const settingsArrived = after.settings !== before.settings;
  const deadStatus = (value) => settingsArrived && !statusesNow.has(value);
  const drop = new Set();
  for (const person of before.people) {
    if (peopleNow.has(person.id)) continue;
    for (const event of [...before.events, ...after.events]) drop.add(cellKey(person.id, event.id));
  }
  for (const event of before.events) {
    if (eventsNow.has(event.id)) continue;
    for (const person of [...before.people, ...after.people]) drop.add(cellKey(person.id, event.id));
  }
  if (drop.size === 0 && !settingsArrived) return after;

  const keys = Object.keys(after.attendance).filter(
    (key) => drop.has(key) || deadStatus(after.attendance[key])
  );
  return keys.length === 0 ? after : { ...after, attendance: without(after.attendance, keys) };
}
